import { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { getAttributionProps } from '@/lib/attribution';
import { MARKETING_EVENTS } from '@/lib/analytics';

/**
 * Fires `marketing_section_viewed` once per section the first time it becomes
 * at least half visible. Observes existing elements by id (non-invasive — no
 * DOM wrapping); pass a stable array of section ids.
 */
export function useSectionViews(sectionIds: string[], path: string) {
  const posthog = usePostHog();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          posthog.capture(MARKETING_EVENTS.sectionViewed, {
            section: entry.target.id,
            path,
            ...getAttributionProps(),
          });
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.5 },
    );

    const els = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    els.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [posthog, path, sectionIds]);
}
